import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateMapArrowInput,
  ListMapArrowsResponse,
  MapArrowDTO,
  PatchMapArrowInput,
} from '@warplan/shared';
import { api } from './api-client.js';

export const MAP_ARROWS_QUERY_KEY = ['map-arrows'] as const;

export function useMapArrows() {
  return useQuery({
    queryKey: MAP_ARROWS_QUERY_KEY,
    queryFn: () => api.get<ListMapArrowsResponse>('/api/arrows'),
    staleTime: 60_000,
  });
}

export function useCreateMapArrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMapArrowInput) =>
      api.post<MapArrowDTO>('/api/arrows', input),
    onSuccess: (created) => {
      qc.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) => {
        if (!prev) return { arrows: [created] };
        if (prev.arrows.some((a) => a.id === created.id)) return prev;
        return { arrows: [...prev.arrows, created] };
      });
    },
  });
}

export function usePatchMapArrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PatchMapArrowInput }) =>
      api.patch<MapArrowDTO>(`/api/arrows/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: MAP_ARROWS_QUERY_KEY });
      const previous = qc.getQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY);
      qc.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return {
          arrows: prev.arrows.map((a) => {
            if (a.id !== id) return a;
            const next: MapArrowDTO = {
              ...a,
              ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
              ...(patch.points !== undefined ? { points: patch.points } : {}),
              ...(patch.color !== undefined ? { color: patch.color } : {}),
              ...(patch.arrowheadStart !== undefined
                ? { arrowheadStart: patch.arrowheadStart }
                : {}),
              ...(patch.arrowheadEnd !== undefined
                ? { arrowheadEnd: patch.arrowheadEnd }
                : {}),
              ...(patch.dashed !== undefined ? { dashed: patch.dashed } : {}),
              ...(patch.metadata !== undefined ? { metadata: patch.metadata } : {}),
            };
            return next;
          }),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(MAP_ARROWS_QUERY_KEY, ctx.previous);
    },
    onSuccess: (updated) => {
      qc.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return { arrows: prev.arrows.map((a) => (a.id === updated.id ? updated : a)) };
      });
    },
  });
}

export function useDeleteMapArrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/arrows/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: MAP_ARROWS_QUERY_KEY });
      const previous = qc.getQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY);
      qc.setQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY, (prev) =>
        prev ? { arrows: prev.arrows.filter((a) => a.id !== id) } : prev,
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(MAP_ARROWS_QUERY_KEY, ctx.previous);
    },
  });
}
